import { useState, type FormEvent } from "react";
import { invalidateApiCache } from "../../../app/api/cache";

interface RegisterFormProps {
  onSuccess: () => void | Promise<void>;
  onSwitchToLogin: () => void;
}

const apiBaseUrl = import.meta.env.VITE_API_URL || "";

export function RegisterForm({
  onSuccess,
  onSwitchToLogin,
}: RegisterFormProps) {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError("");
    setMessage("");
    setIsLoading(true);

    try {
      const res = await fetch(`${apiBaseUrl}/api/auth/sign-up/email`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name, email, password }),
      });
      const result = await res.json().catch(() => null);

      if (!res.ok || result?.error) {
        setError(result?.error?.message ?? result?.error ?? "Registration failed");
        return;
      }

      if (result?.requiresApproval) {
        setMessage(result.message ?? "Account created. An admin must enable it before you can sign in.");
        setPassword("");
        return;
      }

      invalidateApiCache();
      await onSuccess();
    } catch {
      setError("An unexpected error occurred");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="flex flex-col gap-md">
      <div>
        <label
          className="block font-label-sm text-secondary mb-xs"
          htmlFor="reg-name"
        >
          Name
        </label>
        <input
          id="reg-name"
          className="w-full bg-surface-container border border-outline-variant rounded px-3 py-2 font-body-md text-on-surface placeholder:text-secondary/60 focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary transition-colors"
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Your name"
          required
          autoComplete="name"
        />
      </div>

      <div>
        <label
          className="block font-label-sm text-secondary mb-xs"
          htmlFor="reg-email"
        >
          Email
        </label>
        <input
          id="reg-email"
          className="w-full bg-surface-container border border-outline-variant rounded px-3 py-2 font-body-md text-on-surface placeholder:text-secondary/60 focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary transition-colors"
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="you@example.com"
          required
          autoComplete="email"
        />
      </div>

      <div>
        <label
          className="block font-label-sm text-secondary mb-xs"
          htmlFor="reg-password"
        >
          Password
        </label>
        <input
          id="reg-password"
          className="w-full bg-surface-container border border-outline-variant rounded px-3 py-2 font-body-md text-on-surface placeholder:text-secondary/60 focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary transition-colors"
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          placeholder="Choose a password"
          required
          autoComplete="new-password"
          minLength={8}
        />
      </div>

      {error && (
        <div className="font-body-md text-error bg-error-container rounded px-3 py-2">
          {error}
        </div>
      )}

      {message && (
        <div className="font-body-md text-primary bg-surface-container-high rounded px-3 py-2">
          {message}
        </div>
      )}

      <button
        type="submit"
        className="w-full bg-primary text-on-primary py-2.5 rounded font-label-md hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed mt-sm"
        disabled={isLoading}
      >
        {isLoading ? "Creating account…" : "Create account"}
      </button>

      <div className="text-center">
        <button
          type="button"
          className="font-body-md text-secondary hover:text-primary transition-colors underline-offset-2 hover:underline"
          onClick={onSwitchToLogin}
        >
          Already have an account? Sign in
        </button>
      </div>
    </form>
  );
}
